import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";

const MAIN_RE = /static\/js\/main\.[a-zA-Z0-9]+\.js/g;
const CHECK_MS = 5 * 60 * 1000;

const currentBundles = () =>
    [...document.scripts].map((s) => (s.src.match(MAIN_RE) || [])[0]).filter(Boolean);

// Detects a newer deployed build (different main bundle hash in /index.html) and offers a one-tap reload.
export default function UpdateBanner() {
    const [available, setAvailable] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        const check = async () => {
            try {
                const res = await fetch(`/index.html?u=${Date.now()}`, { cache: "no-store" });
                if (!res.ok) return;
                const html = await res.text();
                const remote = html.match(MAIN_RE) || [];
                const local = currentBundles();
                if (alive && remote.length && local.length && !remote.some((r) => local.includes(r))) setAvailable(true);
                navigator.serviceWorker?.getRegistration?.().then((reg) => reg?.update?.()).catch(() => null);
            } catch { /* offline: ignore */ }
        };
        const onVisible = () => document.visibilityState === "visible" && check();
        const t = setInterval(check, CHECK_MS);
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onVisible);
        setTimeout(check, 4000);
        return () => {
            alive = false;
            clearInterval(t);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onVisible);
        };
    }, []);

    const apply = async () => {
        setBusy(true);
        try {
            if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
            }
            const reg = await navigator.serviceWorker?.getRegistration?.();
            reg?.waiting?.postMessage({ type: "SKIP_WAITING" });
        } catch { /* ignore */ }
        window.location.reload();
    };

    if (!available || hidden) return null;
    return (
        <div
            className="fixed left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[380px] z-[60] rounded-2xl bg-stone-900 text-white shadow-2xl p-4 flex items-center gap-3 animate-in slide-in-from-bottom-4"
            role="status"
            data-testid="update-banner"
        >
            <div className="w-10 h-10 rounded-xl bg-white/10 grid place-items-center shrink-0">
                <RefreshCw size={18} className={busy ? "animate-spin" : ""} />
            </div>
            <div className="min-w-0 flex-1">
                <p className="font-bold text-sm">Aggiornamento disponibile</p>
                <p className="text-xs text-stone-300">C'è una nuova versione dell'app. Aggiorna per avere le ultime novità.</p>
            </div>
            <button
                type="button"
                onClick={apply}
                disabled={busy}
                className="shrink-0 px-3 py-2 rounded-xl bg-white text-stone-900 text-sm font-bold active:scale-95 disabled:opacity-60"
                data-testid="update-banner-reload"
            >
                Aggiorna
            </button>
            <button
                type="button"
                onClick={() => setHidden(true)}
                className="shrink-0 p-1 text-stone-400 hover:text-white"
                aria-label="Chiudi"
                data-testid="update-banner-dismiss"
            >
                <X size={16} />
            </button>
        </div>
    );
}
